// Translation
import i18n from "i18next";
// React
import { FunctionComponent, useCallback, useState } from "react";
// Navigation
import { useNavigate } from "react-router-dom";
// Components
import { Badge } from "react-bootstrap";
import { SearchableTable } from "@fyrstain/fhir-front-library";
import PapyrusPage from "../../components/PapyrusPage/PapyrusPage";
// Day js
import dayjs from "dayjs";
// Services
import { PapyrusDocumentRepresentation } from "../../services/document/DocumentModels";
import DocumentReferenceService from "../../services/document/DocumentReferenceService";
import DocumentRepresentationService from "../../services/document/DocumentRepresentationService";

const DOCUMENT_REFERENCE_STATUSES = ["current", "superseded", "entered-in-error"];

const AllDocuments: FunctionComponent = () => {
    const [refreshKey] = useState(0);
    const navigate = useNavigate();

    const onError = useCallback(() => {
        navigate("/Error");
    }, [navigate]);

    function getStatusOption(status: string) {
        return { value: status, label: i18n.t(`document.status.${status}`, status) };
    }

    function formatRepresentations(representations: PapyrusDocumentRepresentation[]) {
        if (!representations.length) {
            return <>-</>;
        }

        return (
            <div className="d-flex flex-wrap gap-1">
                {representations.map((representation) => (
                    <Badge
                        bg="secondary"
                        key={representation.id}
                        role="button"
                        title={representation.contentType}
                        onClick={() => navigate(DocumentRepresentationService.getViewerRoute(
                            representation.documentReferenceId,
                            representation.contentIndex
                        ))}
                    >
                        {i18n.t(representation.labelKey)} ({representation.contentType})
                    </Badge>
                ))}
            </div>
        );
    }

    return (
        <PapyrusPage titleKey="title.alldocuments" loading={false} needsLogin={false}>
            <>
                <SearchableTable
                    key={refreshKey}
                    searchCriteriaProperties={{
                        title: i18n.t("title.searchcriteria"),
                        submitButtonLabel: i18n.t("button.search"),
                        resetButtonLabel: i18n.t("button.reset"),
                        language: i18n.t,
                        fixedParameters: {
                            _sort: "-date",
                        },
                        inputs: [
                            {
                                label: i18n.t("label.subject"),
                                type: "text",
                                placeholder: i18n.t("placeholder.subject"),
                                searchParamsName: "patient",
                            },
                            {
                                label: i18n.t("label.documenttype"),
                                type: "text",
                                placeholder: i18n.t("placeholder.documenttype"),
                                searchParamsName: "type",
                            },
                            {
                                label: i18n.t("label.date"),
                                type: "date",
                                placeholder: i18n.t("placeholder.date"),
                                searchParamsName: "date",
                            },
                            {
                                label: i18n.t("label.status"),
                                type: "select",
                                placeholder: i18n.t("placeholder.status"),
                                options: DOCUMENT_REFERENCE_STATUSES.map(getStatusOption),
                                searchParamsName: "status",
                            },
                            {
                                label: i18n.t("label.mimetype"),
                                type: "text",
                                placeholder: i18n.t("placeholder.mimetype"),
                                searchParamsName: "contenttype",
                            },
                        ],
                    }}
                    paginatedTableProperties={{
                        columns: [
                            {
                                header: i18n.t("label.subject"),
                                dataField: "subject",
                                width: "20%",
                            },
                            {
                                header: i18n.t("label.documenttype"),
                                dataField: "type",
                                width: "20%",
                            },
                            {
                                header: i18n.t("label.date"),
                                dataField: "date",
                                width: "15%",
                                formatter: (cell: string) => {
                                    return <>{cell ? dayjs(cell).fromNow() : "-"}</>;
                                },
                            },
                            {
                                header: i18n.t("label.status"),
                                dataField: "status",
                                width: "12%",
                            },
                            {
                                header: i18n.t("label.mimetypes"),
                                dataField: "representations",
                                width: "33%",
                                formatter: formatRepresentations,
                            },
                        ],
                        action: [],
                        mapResourceToData: async (resource: any) => DocumentReferenceService.toPapyrusDocument(resource),
                        searchProperties: {
                            serverUrl: process.env.REACT_APP_FHIR_URL ?? "fhir",
                            resourceType: "DocumentReference",
                        },
                        onError: onError,
                    }}
                />
            </>
        </PapyrusPage>
    );
};

export default AllDocuments;
